/*************************************************************
 *
 * Tweakwise autocomplete function
 * This function handles autcomplete on the search field in the header.
 */

import axios from "axios";

import debounce from "helpers/debounce/debounce";
import formatPrice from "helpers/format-price/formatPrice";
import { breakPoints } from "helpers/global-variables/globalVariables";
import { trackEvent } from "vendor/google/gtm/gtmEventTracker";

// Config settings
const config = {
  conn: axios.create(), // the axios connection,
  minCharacters: 3,
  form: document.querySelector("#tw-search-form"),
  searchField: document.querySelector(".search-bar [name=tn_q]"), // the search field
  get overlayTemplate() {
    // This is the overlay template that is shown when someone enters a search term in the search field

    const searchurl = this.searchField.getAttribute("data-search-results-url");
    const keyword = this.searchField.value;

    return `<div class="tw-autocomplete"><div class="tw-autocomplete-container">
                <div class="tw-overlay bg-light">
                    <div class="autocomplete-content px-3">
                        <ul class="keywords m-0 p-0 list-unstyled">
                            {{keywords}}
                        </ul>
                        <div class="products">
                            {{products}}
                        </div>
                    </div>
                    <div class="autocomplete-footer">
                        <a 
                            href="${searchurl}?tn_q=${keyword}"
                            class="py-2 px-3 d-inline-block font-weight-bold more-results">
                            ${this.searchField.getAttribute("data-more-results-text")}
                        </a>
                    </div>
                </div>
            </div></div>`;
  },
};

/* Add event listeners */
const addEventListeners = () => {
  if (!config.searchField) {
    return;
  }
  config.searchField.addEventListener(
    "keyup",
    debounce(function (e) {
      handleSearch(e);
    }, 250),
  );

  // Close overlay when user clicks cross inside search input
  config.searchField.addEventListener("search", function () {
    removeTwOverlay();
  });

  config.form.addEventListener("keydown", handleFormKeydown);
  config.form.addEventListener("click", handleFormClicks);
  document.addEventListener("click", function (e) {
    if (e.target.closest(".tw-autocomplete")) {
      handleOverlayClicks(e);
    }

    /* Close the overlay when user clicks outside the overlay */
    if (
      !e.target.closest(".tw-autocomplete") &&
      !e.target.closest(".search-section")
    ) {
      removeTwOverlay();
    }
  });

  window.addEventListener("resize", positionOverlay);
};
/* Handle keydown to send an event to google when enter button is pressed */
const handleFormKeydown = function (e) {
  if (e.keyCode && e.keyCode === 13) {
    trackEvent("Tweakwise", "Enter", "");
  }
};

// Handle clicks to send an event to google when the submit button is clicked */
const handleFormClicks = function (e) {
  const searchButton = e.target.closest(".btn-submit");
  if (searchButton) {
    trackEvent("Tweakwise", "Magnifying glass", "");
  }
};
/* handle clicks inside the overlay to send different events to google */
const handleOverlayClicks = function (e) {
  const suggestion = e.target.closest(".keywords li a");
  const product = e.target.closest(".products a");
  const moreResults = e.target.closest(".more-results");

  if (suggestion) {
    trackEvent("Tweakwise", "Suggestion", "");
  }
  if (product) {
    trackEvent("Tweakwise", "Product", "");
  }
  if (moreResults) {
    trackEvent("Tweakwise", "More results", "");
  }
};

/* get the searchterm and pass if to the fetchresults function,
    if searchterm is empty, remove the overlay
*/
const handleSearch = (e) => {
  const query = encodeURIComponent(e.target.value);
  if (query && query.length >= config.minCharacters) {
    fetchResults(query);
  } else {
    removeTwOverlay();
  }
};

//douche & " <script>

// Create the overlay using the overlaytemplate in the config
const createTwOverlay = (suggestions = "", products = "") => {
  removeTwOverlay();
  let { overlayTemplate: overlayCopy } = config;
  overlayCopy = overlayCopy.replace(/{{keywords}}/g, suggestions);
  overlayCopy = overlayCopy.replace(/{{products}}/g, products);

  document.body.insertAdjacentHTML("beforeend", overlayCopy);
  positionOverlay();
};

// remove the overlay
const removeTwOverlay = () => {
  if (document.querySelector(".tw-autocomplete")) {
    document.body.classList.remove("tw-autocomplete-active");
    document.querySelector(".tw-autocomplete").remove();
  }
};

// show the created overlay
const showTwOverlay = () => {
  if (document.querySelector(".tw-autocomplete-container")) {
    document.body.classList.add("tw-autocomplete-active");
    document.querySelector(".tw-autocomplete-container").classList.add("open");
  }
};

// position the overlay right below the searchfield
const positionOverlay = () => {
  const isMobile = window.matchMedia(
    `(max-width: ${breakPoints.md - 1}px)`,
  ).matches;
  const currentOverlay = document.querySelector(".tw-autocomplete-container");

  if (currentOverlay) {
    if (!isMobile) {
      const searchFieldPosition = config.searchField.getBoundingClientRect();
      const searchContainerPosition =
        config.searchField.parentElement.getBoundingClientRect();

      let overlayWidth = Math.round(searchContainerPosition.width);
      if (overlayWidth < 400) {
        overlayWidth = 400;
      }

      currentOverlay.style.top =
        Math.round(
          searchFieldPosition.top +
            searchFieldPosition.height +
            window.pageYOffset,
        ) -
        1 +
        "px";
      currentOverlay.style.left = Math.round(searchFieldPosition.left) + "px";
      currentOverlay.style.width = overlayWidth + "px";
    } else {
      currentOverlay.style.removeProperty("left");
      currentOverlay.style.removeProperty("top");
      currentOverlay.style.removeProperty("width");
    }
  }
};

// fetch results from ajax-search, this is a proxy that serves the results from the tweakwise api
const fetchResults = (query) => {
  config.conn
    .get(
      `/ajax-search/?tn_q=${query}&lang=${frontendVars.countryCode}&svid=${siteViewId}`,
    )
    .then(function (data) {
      if (data.data) {
        if (data.data.items.length > 0) {
          const products = data.data.items; // get all the products
          const suggestions = data.data.suggestions; // get all the suggestions
          let suggestionData = "";
          let productData = "";
          if (suggestions) {
            suggestionData = renderSuggestions(suggestions); // build the suggestions html
          }
          if (products) {
            productData = renderProducts(products, query); // build the products html
          }
          createTwOverlay(suggestionData, productData); // create the overlay with the correct searchresults
          showTwOverlay();
        } else {
          removeTwOverlay();
        }
      } else {
        removeTwOverlay();
      }
    })
    .catch(function () {
      removeTwOverlay();
    });
};

// build the suggestions html, per row
const renderSuggestions = (data) => {
  let result = "";

  data.forEach(function (suggestion) {
    // Capitalize the title
    const title =
      suggestion.title.substr(0, 1).toUpperCase() +
      suggestion.title.substr(1).toLowerCase();

    result += `<li class="d-block border-bottom">
            <a 
                href="${config.searchField.getAttribute("data-search-results-url")}?tn_q=${suggestion.title}" 
                class="text-primary font-weight-bold py-2 d-block">
                ${title}
            </a>
            </li>`;
  });
  return result;
};

// build the products html, per row
const renderProducts = (data, query) => {
  let result = "";

  data.forEach(function (product) {
    const productTitle =
      product.title.length > 65
        ? product.title.substr(0, 65) + "&hellip;"
        : product.title;
    result += `
                <a href="${product.url}?tn_q=${query}" class="text-primary" title="${product.title}">
                    <div class="row product py-2 border-bottom justify-content-center align-items-center no-gutters">
                        <div class="col-2 pr-3">
                            <img 
                                src="${product.image}" 
                                alt="${product.title}" 
                            />
                        </div>
                        <div class="col-8 pl-2 pr-4">
                            <p class="font-weight-bold m-0">
                                ${productTitle}
                            </p>
                        </div>
                        <div class="col-2 text-right">
                            <span class="price text-primary font-weight-bold">${formatPrice(product.price)}</span>
                        </div>
                    </div>
                </a>`;
  });

  return result;
};

export default {
  init: addEventListeners,
};
